<template>
  <div
    class="bitts-collapse"
    @click.stop="toggleCollapse('header', $event)"
    @auxclick.stop="toggleCollapse('header', $event)"
  >
    <div
      class="bitts-collapse__header"
      :class="{ 'bitts-collapse__header--disabled': isDisabled }"
    >
      <div class="flex items-center w-full pr-4">
        <div class="bitts-collapse__prefix-wrapper">
          <slot name="prefix">
            <button
              v-if="!hideChevron && chevronPlacement === 'left'"
              type="button"
              class="bitts-collapse__toggle"
              :aria-expanded="isOpen"
              :aria-controls="contentId"
              :aria-label="ariaLabel || title || undefined"
              :disabled="isDisabled"
              @click.stop="toggleCollapse('chevron', $event)"
            >
              <FontAwesomeIcon
                :icon="['fak', 'chevron-down']"
                :class="chevronClass"
                class="bitts-collapse__prefix-chevron"
                style="height: 20px; width: 20px"
              />
            </button>
          </slot>
        </div>
        <slot name="title">
          <span class="bitts-collapse__title" :class="[textClass]">
            {{ title }}
          </span>
        </slot>
      </div>
      <slot name="suffix">
        <button
          v-if="!hideChevron && chevronPlacement === 'right'"
          type="button"
          class="bitts-collapse__toggle"
          :aria-expanded="isOpen"
          :aria-controls="contentId"
          :aria-label="ariaLabel || title || undefined"
          :disabled="isDisabled"
          @click.stop="toggleCollapse('chevron', $event)"
        >
          <FontAwesomeIcon
            :icon="['fak', 'chevron-down']"
            :class="chevronClass"
            class="bitts-collapse__suffix-chevron"
            style="height: 20px; width: 20px"
          />
        </button>
      </slot>
    </div>
    <Transition name="collapse">
      <div
        v-if="isOpen || (keepMounted && hasBeenOpened)"
        v-show="isOpen"
        :id="contentId"
        class="bitts-collapse__content-wrapper"
        @click="onContentClick"
        @auxclick="onContentClick"
      >
        <div class="bitts-collapse__content">
          <slot />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';

export type CollapseProps = {
  hideChevron?: boolean;
  title?: string;
  ariaLabel?: string;
  speed?: 'slow' | 'medium' | 'fast';
  chevronPlacement?: 'left' | 'right';
  variant?: 'light' | 'dark';
  keepMounted?: boolean;
  toggleOnBodyClick?: boolean;
  isDisabled?: boolean;
};
const {
  hideChevron,
  title,
  ariaLabel,
  speed = 'medium',
  chevronPlacement = 'left',
  variant = 'light',
  keepMounted = false,
  toggleOnBodyClick = true,
  isDisabled = false,
} = defineProps<CollapseProps>();

const emit =
  defineEmits<
    (
      e: 'clicked-toggle',
      source: 'header' | 'chevron',
      event?: MouseEvent,
    ) => void
  >();

const isOpen = defineModel<boolean>('isOpen');

const contentId = useId();

const hasBeenOpened = ref(!!isOpen.value);
watch(isOpen, (value) => {
  if (value) hasBeenOpened.value = true;
});

function toggleCollapse(source: 'header' | 'chevron', event?: MouseEvent) {
  if (isDisabled) return;
  const shouldOpenInNewTab =
    event?.metaKey || event?.ctrlKey || event?.button === 1;
  if (!shouldOpenInNewTab) {
    isOpen.value = !isOpen.value;
  }
  emit('clicked-toggle', source, event);
}

function onContentClick(event: MouseEvent) {
  if (!toggleOnBodyClick) event.stopPropagation();
}

const transitionDuration = computed(() => {
  return {
    slow: '750ms',
    fast: '200ms',
    medium: '300ms',
  }[speed ?? 'medium'];
});

const chevronClass = computed(() => [
  'ml-auto',
  isOpen.value ? 'rotate-180' : '',
  variant === 'dark' ? 'text-neutral-300' : 'text-neutral-text',
]);

const textClass = computed(() =>
  variant === 'dark' ? 'text-neutral-300' : 'text-neutral-text',
);
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.collapse-enter-active,
.collapse-leave-active {
  @apply transition-all ease-in-out overflow-hidden;
  /* use this for dynamic height */
  transition-duration: v-bind(transitionDuration);
}

.collapse-enter-from,
.collapse-leave-to {
  @apply max-h-0 opacity-0;
}

.collapse-enter-to,
.collapse-leave-from {
  @apply max-h-[720px] opacity-100;
}
.bitts-collapse {
  @apply rounded-lg w-full;
}

.bitts-collapse__header {
  @apply cursor-pointer flex items-center justify-between font-bold w-full h-20;
}

.bitts-collapse__header--disabled {
  @apply cursor-not-allowed;
}

.bitts-collapse__toggle {
  @apply inline-flex items-center justify-center appearance-none border-0 bg-transparent p-0 cursor-pointer;
}

.bitts-collapse__toggle:disabled {
  @apply cursor-not-allowed;
}

.bitts-collapse__toggle:focus-visible {
  @apply outline-2 outline-offset-2 outline-neutral-border-focus rounded-4;
}

.bitts-collapse__content-wrapper {
  @apply overflow-hidden;
}

.bitts-collapse__prefix-wrapper {
  @apply mr-8 h-20;
}

.bitts-collapse__prefix-chevron,
.bitts-collapse__suffix-chevron {
  @apply transition-transform duration-200;
}
</style>
